/**
 * Wounds, as sentences — M2-06's "health is prose and picture, never a number".
 *
 * This is the last mile of AoD §12.3: the sim archives tuples, the narrator
 * renders them, and the body diagram has to be able to say what a wound *is*
 * when a player asks. The bar the ticket sets is a line like
 *
 * > Deep cut, left forearm — bandaged this morning, aching.
 *
 * which is four facts (severity, place, treatment, feeling) and no figures. So
 * every function here turns a number into a word and then throws the number
 * away, and there is deliberately no code path that could print one.
 *
 * The vocabulary is small and fixed on purpose. A player has to be able to sort
 * five severities into an order after seeing them twice, which a thesaurus's
 * worth of adjectives actively prevents.
 */

import { formatGameClock, gameTime, type WoundSnapshot } from '@annwn/protocol';

/**
 * Wound levels as words (M1-02's ladder). Index is the level, so the array is
 * the mapping and there is no table to drift out of step with the sim.
 */
export const SEVERITIES = ['Bruised', 'Light', 'Deep', 'Severe', 'Crippling', 'Mortal'] as const;

export function severityWord(level: number): string {
  return SEVERITIES[Math.max(0, Math.min(SEVERITIES.length - 1, Math.floor(level)))] as string;
}

/**
 * How a part is *drawn*, from the worst wound over it. Coarser than the
 * severity ladder because a diagram is read at a glance and six colours is not
 * a glance.
 */
export type PartState = 'whole' | 'bruised' | 'wounded' | 'serious' | 'ruined';

export function partState(level: number): PartState {
  if (level <= 0) return 'whole';
  if (level === 1) return 'bruised';
  if (level === 2) return 'wounded';
  if (level === 3) return 'serious';
  return 'ruined';
}

/** What kind of wound it is, in words a person would use. */
const TYPES: Readonly<Record<string, string>> = {
  cut: 'cut',
  puncture: 'puncture',
  blunt: 'bruise',
  bite: 'bite',
  burn: 'burn',
};

/** The wound's kind in a person's word: `cut`, `bruise`, `bite`. */
export function woundKind(type: string): string {
  return TYPES[type] ?? type;
}

/** The opening phrase: severity and kind. `Deep cut`, `Crippling bite`. */
export function woundTitle(wound: WoundSnapshot, partName: string): string {
  return `${severityWord(wound.level)} ${woundKind(wound.type)}, ${partName}`;
}

/**
 * How long ago, as a person would say it — not as a duration.
 *
 * "This morning" is the register the ticket asks for, and it is also the only
 * honest one: the sim knows the tick, and a player does not carry a clock.
 */
export function whenPhrase(inflictedAt: number, now: number): string {
  const then = gameTime(Math.max(0, inflictedAt));
  const elapsed = gameTime(Math.max(0, now)).day - then.day;

  if (elapsed >= 2) return `${elapsed} days ago`;
  if (elapsed === 1) return 'yesterday';

  const hour = then.hour;
  if (hour < 5) return 'in the small hours';
  if (hour < 12) return 'this morning';
  if (hour < 17) return 'this afternoon';
  if (hour < 21) return 'this evening';
  return 'tonight';
}

/** What it is doing right now: bleeding, festering, or knitting. */
export function conditionPhrase(wound: WoundSnapshot): string {
  // Festering trumps everything — it is the thing a treater needs to know first.
  if (wound.infection !== null && wound.infection !== 'clean') {
    return `${wound.infection} and hot to the touch`;
  }
  if (wound.bleedStage === 'heavy') return 'bleeding freely';
  if (wound.bleedStage === 'open') return 'still bleeding';

  // How it was closed, when it was closed deliberately (M4-02). The method is
  // the story: a seared wound and a sutured one heal and scar differently.
  if (wound.cauterized) return 'seared shut';
  if (wound.sutured) return 'stitched, and knitting';

  // Bound but not yet washed reads differently from bound and clean — the
  // difference cleaning makes, said in words rather than a number (M4-02).
  if (wound.state === 'treated') return wound.dirty ? 'bound up, but grimed' : 'cleaned and bound';
  if (wound.state === 'healing') return 'closing over';
  return 'closed, and aching';
}

/** The effects worth naming in a sentence. Silent about the ones that are not. */
const EFFECTS: Readonly<Record<string, string>> = {
  sever: 'taken off entirely',
  break: 'the bone broken',
  blind: 'the eye gone',
};

/**
 * The full record a player sees when they click a part.
 *
 * Built as clauses and joined, rather than as one template per case: the number
 * of combinations is severity × type × treatment × provenance, and a template
 * per combination is a corpus nobody can keep true.
 */
export function describeWound(
  wound: WoundSnapshot,
  partName: string,
  now: number,
): { readonly title: string; readonly detail: string } {
  const clauses: string[] = [conditionPhrase(wound)];

  for (const effect of wound.effects) {
    const phrase = EFFECTS[effect];
    if (phrase) clauses.push(phrase);
  }

  // Something still in there (M4-05). Only ever set for an examiner with the
  // medicine to see it — the server decides that, not this — so the clause is
  // printed whenever it arrives, and its arrival *is* the skill.
  if (wound.lodged) clauses.push('something still in it');

  // Provenance, then when, then where — the order a person tells it in.
  const story = [provenancePhrase(wound), whenPhrase(wound.inflictedAt, now)];
  const place = placePhrase(wound.where);
  if (place) story.push(place);

  return {
    title: woundTitle(wound, partName),
    detail: `${capitalize(clauses.join(', '))}. ${capitalize(story.join(', '))}.`,
  };
}

/**
 * A healed wound, as one line of biography (M4-04).
 *
 * The whole of what a scar is in the MVP: no mechanics, no hover panel, no
 * numbers — a sentence saying what it was, who gave it to you and how long ago.
 * "An old spear-scar, right thigh" is the ticket's own example, and the reason
 * it can be written at all is that the wound record kept its provenance from the
 * moment it was inflicted (M1-02) rather than acquiring one when it closed.
 *
 * How it was closed changes the noun, because it changes the mark: a sutured cut
 * leaves a seam and an iron leaves a burn, and a player who chose the iron
 * should be reading about it years later.
 */
export function scarLine(scar: WoundSnapshot, partName: string, now: number): string {
  const kind = TYPES[scar.type] ?? scar.type;
  const mark = scar.cauterized
    ? 'burn-scar'
    : scar.sutured
      ? `seamed ${kind}-scar`
      : `${kind}-scar`;
  const age = scar.healedAt > 0 ? whenPhrase(scar.healedAt, now) : null;

  const by = scar.by ?? null;
  const from = by ? `${by}'s doing` : provenancePhrase(scar);

  // "An old" only once it genuinely is old; a scar from this morning is not.
  const article = age === null || age.endsWith('ago') ? 'An old' : 'A';
  return [`${article} ${mark}, ${partName}`, from, age].filter((part) => part !== null).join(' — ');
}

/**
 * Who did it, and with what. The half of AoD §12.3 that turns a scar into a
 * story a player can tell somebody else three milestones later.
 *
 * The article is stripped off a named weapon so the possessive reads: "a seax"
 * belonging to Bran is "Bran's seax", not "a seax, Bran's".
 */
export function provenancePhrase(wound: WoundSnapshot): string {
  if (wound.by && wound.weapon) return `${wound.by}'s ${bareNoun(wound.weapon)}`;
  if (wound.by) return `${wound.by}'s doing`;
  if (wound.weapon) return wound.weapon;
  if (wound.cause === 'beast') return 'something in the wood';
  if (wound.cause === 'fall') return 'a fall';
  return 'come by somehow';
}

/**
 * Where it happened — when the sim has somewhere to name.
 *
 * The live combat path currently stamps a grid reference, because places do not
 * have names until worldgen gives them any (M3-01). A player should never be
 * shown a coordinate pair, so anything without a letter in it is dropped rather
 * than printed: the record keeps it, and the sentence waits for a real name.
 */
export function placePhrase(where: string): string | null {
  return /[a-z]/i.test(where) ? where : null;
}

/** `a seax` → `seax`, so it can take a possessive in front of it. */
function bareNoun(name: string): string {
  return name.replace(/^(a|an|the)\s+/i, '');
}

/** Blood, as the state a bystander would describe (M1-04's rungs). */
const BLOOD_PROSE: Readonly<Record<string, string>> = {
  hale: 'steady on your feet',
  pale: 'pale, and cold at the fingers',
  failing: 'grey, and the edges are going soft',
  dying: 'white as chalk',
  dead: 'gone',
};

export function bloodProse(stage: string): string {
  return BLOOD_PROSE[stage] ?? stage;
}

/** Wind, as a word. The band names are the sim's; these are the readings. */
const BAND_PROSE: Readonly<Record<string, string>> = {
  fresh: 'breathing easy',
  labouring: 'labouring',
  winded: 'winded',
};

export function windProse(band: string): string {
  return BAND_PROSE[band] ?? band;
}

/** The game clock, for a line that wants to say when rather than how long ago. */
export function clockAt(tick: number): string {
  return formatGameClock(gameTime(Math.max(0, tick)));
}

function capitalize(text: string): string {
  return text.slice(0, 1).toUpperCase() + text.slice(1);
}
